import { useState, useCallback, useContext, useMemo } from "react";
import { PageStateContext } from "../PageContext";
import { debugLog } from "../utils";

/**
 * Glossary-handler hook
 * • Collects glossary IDs referenced in rich-text anchors
 * • Exposes helpers to validate / fetch terms
 * • Now logs every decision so you can trace why a link is kept or dropped
 *
 * ⚠️  PagedJS / static PDF gotcha
 * ─────────────────────────────────
 * `glossaryIds` is populated via a useEffect in RichText.js that fires AFTER
 * the initial render.  In static Gatsby PDF mode, PagedJS (Paginator.js) also
 * runs in a useEffect and captures the DOM on first paint — before the
 * setGlossaryIds state update has propagated.  This means any component that
 * renders GlossaryTerms using this hook's `glossaryIds` will appear empty in
 * the paginated PDF output.
 *
 * ✅  Instead: compute glossary IDs synchronously via useMemo at the component
 * level, scanning the resolved content tree for anchor hrefs with
 * `extractTermIdFromHref` + `isValidTermId`.  See Topic.js for the pattern.
 *
 * The hook's `glossaryIds` / `handleGlossaryAnchor` are still useful in
 * non-paginated contexts (previews, web view) where a post-render state update
 * is fine.
 */
const useGlossaryHandler = () => {
  const [glossaryIds, setGlossaryIds] = useState([]);
  const { pageState } = useContext(PageStateContext);
  const { glossary } = pageState || {};

  const glossaryIdSet = useMemo(() => {
    return new Set((glossary || []).map(g => Number(g.id))); // useMemo so we only do this once to build our map of glossaryTermIds
  }, [glossary]);

  /* ────────────────────────────────────────────────────────────────── */
  /*  ID extractor                                                     */
  /* ────────────────────────────────────────────────────────────────── */
  const extractTermIdFromHref = useCallback(href => {
    debugLog("[Glossary] ⟶ raw href:", href);
    if (!href) {
      debugLog("[Glossary] • empty href → bail");
      return null;
    }

    /* Flag: was the original attribute URI-encoded “%7Bentry:…” ? */
    const originallyEncoded =
      /%7Bentry%3A/i.test(href) || /%257Bentry%253A/i.test(href);

    /* Decode up to two layers */
    let decoded = href;
    for (let i = 0; i < 2; i += 1) {
      try {
        const tmp = decodeURIComponent(decoded);
        if (tmp === decoded) break;
        decoded = tmp;
      } catch {
        break;
      }
    }
    debugLog("[Glossary] • decoded:", decoded);

    /* Quick bail-out – only glossary links are interesting            *
     * (variables are already stripped in resolveEntry)                */
    const looksLikeGlossary =
      /\/(?:digiguide\/)?glossary\/\d+/i.test(decoded) ||
      decoded.includes("#entry:") ||
      decoded.includes("{entry:");

    if (!looksLikeGlossary) {
      debugLog("[Glossary] • no glossary hint → bail");
      return null;
    }

    /* Remove query string */
    if (decoded.includes("?")) decoded = decoded.split("?")[0];

    /* 1) Absolute “…/digiguide/glossary/123” */
    let m = decoded.match(/\/digiguide\/glossary\/(\d+)/i);
    if (m) {
      debugLog("[Glossary] • abs match id:", m[1]);
      return m[1];
    }

    /* 2) Relative “…/glossary/123” */
    m = decoded.match(/\/glossary\/(\d+)/i);
    if (m) {
      debugLog("[Glossary] • rel match id:", m[1]);
      return m[1];
    }

    /* 3) “#entry:123” anchors  (used in paginated mode) */
    if (decoded.includes("#entry:")) {
      const id = decoded.split("#entry:")[1].replace(/[^\d].*$/, "");
      debugLog("[Glossary] • hash anchor id:", id);
      return id;
    }

    /* 4) {entry:…} ref-tag                                         *
     *    Accept only if                                            *
     *      – the original attr was URI-encoded  -or-               *
     *      – the tag itself includes “/glossary/<id>”              */
    if (decoded.startsWith("{entry:")) {
      const hasGlossaryInside = /\/glossary\/\d+/i.test(decoded);
      if (!originallyEncoded && !hasGlossaryInside) {
        debugLog("[Glossary] • ref-tag but NOT glossary → bail");
        return null;
      }

      let termId = decoded
        .replace(/^\{entry:/, "")
        .replace(/\|.*$/, "") // drop “:url||…”
        .replace(/\}$/, "");

      if (termId.includes("@")) termId = termId.split("@")[0];
      termId = termId.replace(/[^\d]*$/, "");

      debugLog("[Glossary] • ref-tag id:", termId);
      return termId || null;
    }

    debugLog("[Glossary] • fell through → bail");
    return null;
  }, []);

  /* ────────────────────────────────────────────────────────────────── */
  /*  Public helpers                                                   */
  /* ────────────────────────────────────────────────────────────────── */

  const handleGlossaryAnchor = useCallback(
    href => {
      const termId = extractTermIdFromHref(href);
      debugLog("[Glossary] handleAnchor → extracted:", termId);
      setGlossaryIds(cur => {
        const idNum = Number(termId);
        if (!Number.isFinite(idNum) || cur.includes(idNum)) return cur;
        return [...cur, idNum];
      });
    },
    [extractTermIdFromHref]
  );

  const isValidTermId = useCallback(
    termId => glossaryIdSet.has(Number(termId)),
    [glossary]
  );

  const getGlossaryTerm = useCallback(
    termId => glossary?.find(t => Number(t.id) === Number(termId)) || null,
    [glossary]
  );

  return {
    glossaryIds,
    handleGlossaryAnchor,
    isValidTermId,
    getGlossaryTerm,
    extractTermIdFromHref, // handy in unit tests / console
  };
};

export default useGlossaryHandler;
